import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPosts, createPost, likePost, commentOnPost, uploadMedia } from '../api';

const FeedPage = () => {
  const { user, auth } = useAuth();
  const [posts, setPosts] = useState([]);
  const [caption, setCaption] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPosts = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const data = await getPosts(token);
      setPosts(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      let mediaUrl = '';
      let mediaType = 'text';
      if (mediaFile) {
        const base64 = await fileToBase64(mediaFile);
        const result = await uploadMedia({ base64, resourceType: mediaFile.type.startsWith('video') ? 'video' : 'image' }, token);
        mediaUrl = result.url;
        mediaType = mediaFile.type.startsWith('video') ? 'video' : 'image';
      }
      await createPost({ caption, mediaUrl, mediaType }, token);
      setCaption('');
      setMediaFile(null);
      loadPosts();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    const token = await auth.currentUser.getIdToken();
    await likePost(postId, token);
    loadPosts();
  };

  const addComment = async (postId) => {
    const text = prompt('Enter your comment');
    if (!text) return;
    const token = await auth.currentUser.getIdToken();
    await commentOnPost(postId, text, token);
    loadPosts();
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  return (
    <div className="bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-secondary mb-2">📰 Your Feed</h1>
          <p className="text-text-light text-lg">Share your moments and discover what's trending</p>
        </div>
        
        {/* Create Post Form */}
        <form onSubmit={handleSubmit} className="bg-surface rounded-2xl shadow-lg p-8 mb-8 border-l-4 border-primary">
          <h2 className="text-2xl font-bold text-secondary mb-6">✨ Share Something New</h2>
          
          <div className="mb-6">
            <label className="block text-text font-semibold mb-3 text-lg">What's on your mind?</label>
            <textarea 
              value={caption} 
              onChange={(e) => setCaption(e.target.value)} 
              placeholder="Write something inspiring..." 
              rows={4}
              className="w-full border-2 border-gray-300 rounded-xl p-4 focus:border-primary focus:outline-none text-lg resize-none"
            />
          </div>

          <div className="mb-6">
            <label className="block text-text font-semibold mb-3 text-lg">Add Media (optional)</label>
            <label className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-primary hover:bg-primary/5 transition flex flex-col items-center justify-center">
              <svg className="w-12 h-12 text-text-light mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-text-light font-medium">Click to upload image or video</span>
              <input 
                type="file" 
                accept="image/*,video/*" 
                onChange={(e) => setMediaFile(e.target.files[0])}
                className="hidden"
              />
            </label>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-gradient-to-r from-primary to-primary/80 text-surface font-bold py-3 rounded-xl hover:shadow-lg transition disabled:bg-gray-400"
          >
            {loading ? '⏳ Posting...' : '🚀 Post Now'}
          </button>
        </form>

        {/* Posts List */}
        <div className="space-y-6">
          {posts.length === 0 ? (
            <div className="bg-surface rounded-2xl shadow-md p-12 text-center border-l-4 border-accent">
              <p className="text-text-light text-lg">No posts yet. Be the first to share! 🎉</p>
            </div>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="bg-surface rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition border-l-4 border-secondary">
                <div className="p-6 md:p-8">
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <p className="text-text-light text-sm font-semibold">Posted {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'recently'}</p>
                  </div>

                  <p className="text-text text-lg mb-4 leading-relaxed">{post.caption}</p>
                  
                  {post.mediaUrl && post.mediaType === 'image' && (
                    <img src={post.mediaUrl} alt="post" className="w-full rounded-xl mb-4 shadow-md hover:shadow-lg transition" />
                  )}
                  
                  {post.mediaUrl && post.mediaType === 'video' && (
                    <video controls className="w-full rounded-xl mb-4 shadow-md">
                      <source src={post.mediaUrl} type="video/mp4" />
                    </video>
                  )}

                  <div className="flex gap-4 pt-4 border-t border-gray-200">
                    <button 
                      onClick={() => handleLike(post.id)}
                      className="flex-1 bg-primary/10 hover:bg-primary hover:text-surface text-primary font-semibold py-2 rounded-lg transition flex items-center justify-center gap-2"
                    >
                      ❤️ Like ({post.likes?.length || 0})
                    </button>
                    <button 
                      onClick={() => addComment(post.id)}
                      className="flex-1 bg-accent/10 hover:bg-accent hover:text-surface text-accent font-semibold py-2 rounded-lg transition flex items-center justify-center gap-2"
                    >
                      💬 Comment
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedPage;
